import React, { useState } from 'react';
import { Select } from '../../components/controls/Select.jsx';
import { useSessionState } from '../../lib/useSessionState.js';
import { sanitizeMonth } from '../../lib/sessionState.js';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import {
  fmtStamp,
  monthLabel,
  monthOptions,
  recentMonths,
  runUsageJob,
  thisMonth,
} from '../../lib/usage.js';
import { SEGMENT_LABEL } from '../../lib/platformOrgs.js';
import {
  breakdownLabel,
  breakdownOf,
  expandableMonths,
  fmt,
  sumVoice,
} from '../../lib/usageTable.js';
import { useClientUsage } from './useClientUsage.js';

const CELL_CLASS = [
  'num sep usage-calls',
  'num usage-minutes',
  'num usage-minutes',
];

function VoiceCells({ rows }) {
  return sumVoice(rows).map((value, i) => (
    <td key={i} className={CELL_CLASS[i % 3]}>
      {fmt(value)}
    </td>
  ));
}

export function VoiceTable({ months, orgs, expanded, toggle }) {
  return (
    <div className="table-wrap usage-table">
      <table>
        <thead>
          <tr>
            <th rowSpan={2} scope="col">
              Month
            </th>
            {['Inbound', 'Outbound', 'Total'].map((group) => (
              <th
                key={group}
                colSpan={3}
                scope="colgroup"
                className="usage-group sep"
              >
                {group}
              </th>
            ))}
          </tr>
          <tr>
            {[0, 1, 2].map((g) =>
              ['Calls', 'Duration (min)', 'Tarification (min)'].map((h, i) => (
                <th key={`${g}${h}`} scope="col" className={CELL_CLASS[i]}>
                  {h}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {months.map((m) => {
            const breakdown = breakdownOf(m);
            const open = expanded.has(m.month);
            return (
              <React.Fragment key={m.month}>
                <tr>
                  <td className="usage-month">
                    <span className="usage-month-label">
                      {breakdown.length > 0 ? (
                        <button
                          type="button"
                          className="usage-expand"
                          aria-expanded={open}
                          aria-label={`${open ? 'Collapse' : 'Expand'} ${monthLabel(m.month)}`}
                          onClick={() => toggle(m.month)}
                        >
                          {open ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </button>
                      ) : (
                        <span className="usage-expand-spacer" />
                      )}
                      {monthLabel(m.month)}
                      {!m.complete && (
                        <span className="usage-partial">Partial</span>
                      )}
                    </span>
                    <small>computed {fmtStamp(m.computedAt)}</small>
                  </td>
                  <VoiceCells rows={m.rows} />
                </tr>
                {open &&
                  breakdown.map((r) => (
                    <tr
                      key={`${r.organizationId}|${r.segment}`}
                      className="usage-breakdown-row"
                    >
                      <td>{breakdownLabel(r, orgs)}</td>
                      <VoiceCells rows={[r]} />
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
          {months.length > 1 && (
            <tr className="usage-total-row">
              <td className="usage-month">
                <span className="usage-month-label">
                  <span className="usage-expand-spacer" />
                  Total · {months.length} months
                </span>
              </td>
              <VoiceCells rows={months.flatMap((m) => m.rows)} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function RecomputeBar({ clientId, months, sourceMonth, onDone }) {
  const [pickedMonth, setMonth] = useSessionState(
    `client.${clientId}.month`,
    null,
    sanitizeMonth,
  );
  const month = pickedMonth ?? recentMonths(2, sourceMonth)[1];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const stored = months.find((m) => m.month === month);

  async function recompute() {
    setBusy(true);
    setError('');
    try {
      await runUsageJob(`/clients/${clientId}/usage/recompute`, {
        month,
        source: 'voice_calls',
      });
      await onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="usage-recompute">
      <label>
        <span>Month</span>
        <Select
          ariaLabel="Month"
          value={month}
          onChange={setMonth}
          disabled={busy}
          options={monthOptions(month, 12, sourceMonth).map((m) => ({
            value: m,
            label: `${monthLabel(m)}${
              m === thisMonth(sourceMonth) ? ' (in progress)' : ''
            }`,
          }))}
        />
      </label>
      <button
        type="button"
        className="secondary"
        onClick={recompute}
        disabled={busy}
      >
        <RefreshCw size={14} /> {busy ? 'Recomputing…' : 'Recompute'}
      </button>
      <span className="muted-text">
        This client only ·{' '}
        {stored
          ? `last computed ${fmtStamp(stored.computedAt)} by ${stored.computedBy || '—'}`
          : 'not computed yet'}
      </span>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

export function ClientTransactionTab({ client, data, onOpenInfo }) {
  const { usage, loaded, error, reload } = useClientUsage(client.id);
  const [expanded, setExpanded] = useState(() => new Set());
  const isAdmin = Boolean(data.canManageAll);
  const configured = usage.sources.voice_calls?.configured;
  const voiceMonths = usage.months.filter((m) => m.source === 'voice_calls');
  const expandable = expandableMonths(voiceMonths);
  const allOpen =
    expandable.length > 0 && expandable.every((m) => expanded.has(m));

  const toggle = (month) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });

  if (!loaded)
    return (
      <div className="empty-panel">
        {error ? (
          <>
            Couldn’t load transaction data.{' '}
            <button
              type="button"
              className="secondary small"
              onClick={() => reload().catch(() => {})}
            >
              Retry
            </button>
          </>
        ) : (
          'Loading…'
        )}
      </div>
    );

  const linked = usage.orgs.length > 0;

  return (
    <div className="client-billing-tab">
      {isAdmin && !configured && (
        <div className="calendar-connect-hint warning">
          <AlertTriangle size={16} />
          <div>
            The voice call data source isn’t configured on this server, so
            months can’t be recomputed. Stored months still show below.
          </div>
        </div>
      )}
      {isAdmin && configured && (linked || voiceMonths.length > 0) && (
        <RecomputeBar
          clientId={client.id}
          months={voiceMonths}
          sourceMonth={usage.sources.voice_calls?.currentMonth}
          onDone={reload}
        />
      )}

      <section className="card">
        <div className="card-head usage-card-head">
          <h2>Voice calls</h2>
          {expandable.length > 0 && (
            <button
              type="button"
              className="text-button small"
              onClick={() => setExpanded(new Set(allOpen ? [] : expandable))}
            >
              {allOpen ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
        </div>
        <div className="usage-linked">
          {linked ? (
            <>
              Usage of
              {usage.orgs.map((o) => (
                <span className="usage-chip" key={o.organizationId}>
                  {o.label || o.organizationId}
                  {o.segmentBy !== 'none' &&
                    ` · ${SEGMENT_LABEL[o.segmentBy].toLowerCase()}`}
                </span>
              ))}
            </>
          ) : (
            'No platform organization is linked to this client yet.'
          )}
          <button type="button" className="link-button" onClick={onOpenInfo}>
            {linked ? 'Linked in Client Info' : 'Link one in Client Info'}
          </button>
        </div>
        {voiceMonths.length === 0 ? (
          <p className="muted-text">No month has been computed yet.</p>
        ) : (
          <>
            <div className="usage-legend">
              <span>
                <b>Duration</b> — exact call time (seconds ÷ 60)
              </span>
              <span>
                <b>Tarification</b> — every call rounded up to a whole minute
              </span>
            </div>
            <VoiceTable
              months={voiceMonths}
              orgs={usage.orgs}
              expanded={expanded}
              toggle={toggle}
            />
          </>
        )}
        <p className="muted-text billing-rule">
          Calls with a non-zero duration, by call start. Months never computed
          are not listed.
        </p>
      </section>
    </div>
  );
}
